import type {
    AnalyticsProperties,
    AnalyticsProvider,
    PageViewProperties,
} from '../types';

const GTM_SCRIPT_ATTR = 'data-bio-analytics-gtm';

function ensureDataLayer(): Record<string, unknown>[] {
    if (typeof window === 'undefined') {
        return [];
    }
    if (!Array.isArray(window.dataLayer)) {
        window.dataLayer = [];
    }
    return window.dataLayer;
}

/**
 * Injects the GTM bootstrap script once per container id.
 * No-op if the script is already present (idempotent).
 */
export function loadGtmContainer(containerId: string): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return;
    }

    const id = containerId.trim();
    if (!id) {
        return;
    }

    ensureDataLayer();

    if (document.querySelector(`script[${GTM_SCRIPT_ATTR}="${id}"]`)) {
        return;
    }

    ensureDataLayer().push({
        'gtm.start': new Date().getTime(),
        event: 'gtm.js',
    });

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(id)}`;
    script.setAttribute(GTM_SCRIPT_ATTR, id);

    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript?.parentNode) {
        firstScript.parentNode.insertBefore(script, firstScript);
    } else {
        document.head.appendChild(script);
    }
}

/**
 * Loads GTM (when initialized) and pushes events to window.dataLayer.
 * `page_location` is built by AnalyticsService before pageView is called.
 */
export class GtmProvider implements AnalyticsProvider {
    readonly name = 'gtm' as const;

    constructor(private readonly containerId: string) {}

    initialize(): void {
        loadGtmContainer(this.containerId);
    }

    track(event: string, properties: AnalyticsProperties = {}): void {
        this.push({
            event,
            ...properties,
        });
    }

    pageView(
        _path: string,
        properties:
            | (Partial<PageViewProperties> & AnalyticsProperties)
            | undefined = {}
    ): void {
        this.push({
            event: 'screen_view',
            ...properties,
        });
    }

    private push(payload: Record<string, unknown>): void {
        if (typeof window === 'undefined') {
            return;
        }
        ensureDataLayer().push(payload);
    }
}
